// ── EVIDENCE MONITORING S3 — "Check for Evidence Updates", one location, on the Control Hub Evidence tab ─
//
// ⚠ SEPARATE FROM "Check connection health" (Settings → Evidence repositories), which probes the whole
//   repository connection. This asks VeriGRC to look in THIS control's folder now, instead of waiting for
//   the next daily check: the same recheck the schedule runs, for one location.
// ⚠ WARNING → ACKNOWLEDGE → CONFIRM, in the existing dialog (components/ui/dialog) — no new framework.
// ⚠ THE STATUS IS THE SERVER'S. Queued / running come from the queue; every result is the check run's own
//   outcome. The status hook polls only while the check is pending, and refreshes the location and the
//   evidence list once it finishes (useEvidenceCheckStatus).

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useEvidenceCheckStatus, useRequestEvidenceCheck } from '@/api/hooks/useEvidenceProvisioning';
import type { EvidenceCheckState, EvidenceLocation } from '@/api/types/evidence-provisioning.types';
import { apiErrorMessage } from './provisioning-copy';

export const CHECK_ACTION = 'Check for evidence updates';

export const CHECK_WARNING =
  'VeriGRC will look in this control’s evidence folder now, instead of waiting for the next daily check. '
  + 'Files added since the last check are captured by VeriGRC; changes to recorded files are flagged for review. '
  + 'Nothing in your folder is moved or deleted. You can ask once every 10 minutes.';

export const CHECK_ACKNOWLEDGE = 'I understand VeriGRC will read this folder now';

/** Every state named; an unknown one says so rather than guessing the reassuring option. */
export function checkStateLabel(state: EvidenceCheckState): string | null {
  switch (state) {
    case 'none':            return null;
    case 'queued':          return 'Update check queued';
    case 'running':         return 'Checking the folder now…';
    case 'completed':       return 'Update check complete';
    case 'nothing_checked': return 'Update check finished, but nothing in this folder could be checked';
    case 'partial':         return 'Update check incomplete — part of the folder could not be read; it will be retried';
    case 'throttled':       return 'Update check paused by the repository provider; it will be retried';
    case 'failed':          return 'Update check failed';
    case 'stalled':         return 'Update check did not finish';
    default:                return 'Update check result not available';
  }
}

const PENDING = new Set<EvidenceCheckState>(['queued', 'running']);
const USABLE = new Set(['provisioned', 'adopted']);

/** ⚠ Offered only when the server-reported location can actually be rechecked (same gates as the route). */
export function canCheckForUpdates(loc: EvidenceLocation | null, canProvision: boolean, connected: boolean): boolean {
  return Boolean(canProvision && connected && loc && USABLE.has(loc.provisioning_state) && loc.has_surface
    && loc.baseline_state === 'baselined' && loc.binding_validation_state === 'validated');
}

export function EvidenceUpdateCheck({ reference, location, canProvision, connected }: {
  reference: string; location: EvidenceLocation | null; canProvision: boolean; connected: boolean;
}) {
  const baselined = location?.baseline_state === 'baselined';
  const status = useEvidenceCheckStatus(reference, baselined);
  const request = useRequestEvidenceCheck();
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const state: EvidenceCheckState = status.data?.state ?? 'none';

  const close = () => { setOpen(false); setAck(false); };
  const confirm = () => {
    setError(null);
    request.mutate({ reference }, {
      onSuccess: () => close(),
      onError: (err) => { setError(apiErrorMessage(err, 'The check could not be requested.')); close(); },
    });
  };

  const label = checkStateLabel(state);
  const offered = canCheckForUpdates(location, canProvision, connected);
  if (!label && !offered && !error) return null;
  const cooling = (status.data?.cooldown_seconds_remaining ?? 0) > 0;

  return (
    <div className="mt-[6px]" data-testid="evidence-update-check">
      {label && (
        <p className="text-[11.5px] text-text-secondary" data-testid="evidence-update-check-status" data-state={state}>
          {label}
          {status.data?.finished_at && !PENDING.has(state) && (
            <span className="text-text-faint">{' · '}{new Date(status.data.finished_at).toLocaleString()}</span>
          )}
        </p>
      )}
      {error && <p className="mt-[3px] text-[12px] text-cert-warn-fg" role="alert">{error}</p>}
      {offered && (
        <Button variant="outline" size="sm" className="mt-[5px]" data-testid="evidence-update-check-open"
                disabled={PENDING.has(state) || cooling || request.isPending}
                onClick={() => { setError(null); setOpen(true); }}>
          {CHECK_ACTION}
        </Button>
      )}
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent data-testid="evidence-update-check-dialog">
          <DialogHeader>
            <DialogTitle>{CHECK_ACTION}?</DialogTitle>
            <DialogDescription>{reference}</DialogDescription>
          </DialogHeader>
          <p className="text-sm" data-testid="evidence-update-check-warning">{CHECK_WARNING}</p>
          <div className="flex items-center gap-2">
            <Checkbox id="evidence-update-check-ack" checked={ack} onCheckedChange={setAck} />
            <Label htmlFor="evidence-update-check-ack">{CHECK_ACKNOWLEDGE}</Label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button data-testid="evidence-update-check-confirm" disabled={!ack || request.isPending} onClick={confirm}>
              {request.isPending ? 'Requesting…' : 'Check now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
